import React from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  IconButton,
  Alert,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { useScheduledProcedureCodeAddDialog } from './hooks';
import { ProcedureInfoSection, DialogActionButtons } from './components';

interface ScheduledProcedureCodeAddDialogProps {
  onScheduledProcedureCodeAdded?: () => void;
}

const ScheduledProcedureCodeAddDialog: React.FC<ScheduledProcedureCodeAddDialogProps> = ({
  onScheduledProcedureCodeAdded
}) => {
  const {
    open,
    procedures,
    isEncounterCancelled,
    handleOpen,
    handleClose,
    handleSubmit,
    formData,
    selectedProcedure,
    error,
    handleProcedureChange,
    handleInputChange,
  } = useScheduledProcedureCodeAddDialog({ onScheduledProcedureCodeAdded });

  // Don't render if encounter is cancelled
  if (isEncounterCancelled) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={handleOpen}
        sx={{
          backgroundColor: '#162346',
          '&:hover': {
            backgroundColor: '#1a2a52',
          },
          fontWeight: 600,
          textTransform: 'none',
          borderRadius: 1.5,
          px: 2,
          py: 1
        }}
      >
        Add Scheduled Procedure Code
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#ffffff',
          borderBottom: '2px solid #3b82f6',
          p: 2.5,
          position: 'relative'
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Add sx={{ color: '#3b82f6', fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Add Scheduled Procedure Code
            </Typography>
          </Stack>
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#64748b',
              '&:hover': {
                backgroundColor: '#f1f5f9'
              }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
          <Stack spacing={3}>
            <ProcedureInfoSection
              procedures={procedures}
              selectedProcedure={selectedProcedure}
              code={formData.code}
              description={formData.description}
              onProcedureChange={handleProcedureChange}
              onDescriptionChange={(value) => handleInputChange('description', value)}
            />

            {/* Error Message Section */}
            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <DialogActionButtons
            onCancel={handleClose}
            onSubmit={handleSubmit}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScheduledProcedureCodeAddDialog;
